import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardBannerAPI, seasonalThemeAPI } from '@/lib/api';
import { presetHero } from '@/lib/seasonalThemes';
import { ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const ROTATE_MS = 8000;
const FINE_DETAIL_DAYS = 45;

const imgSrc = (url) => (url ? (/^https?:\/\//i.test(url) ? url : `${BACKEND}${url}`) : null);

function useNow(active) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [active]);
  return now;
}

function CountdownValue({ targetAt, now }) {
  const targetMs = targetAt ? new Date(targetAt).getTime() : 0;
  const diff = targetMs - now;
  if (!targetMs || diff <= 0) return null;
  const days = Math.floor(diff / 86400000);
  if (days > FINE_DETAIL_DAYS) {
    return (
      <div className="flex items-end gap-2" data-testid="banner-countdown-days-only">
        <span className="text-4xl md:text-6xl font-extrabold tabular-nums leading-none">{days}</span>
        <span className="text-sm md:text-lg font-semibold uppercase tracking-wide opacity-90 mb-1">days</span>
      </div>
    );
  }
  const hours = Math.floor((diff % 86400000) / 3600000);
  const minutes = Math.floor((diff % 3600000) / 60000);
  const seconds = Math.floor((diff % 60000) / 1000);
  const cells = [
    { v: days, l: 'days' },
    { v: hours, l: 'hrs' },
    { v: minutes, l: 'min' },
    { v: seconds, l: 'sec' },
  ];
  return (
    <div className="flex items-center gap-2 md:gap-4" data-testid="banner-countdown-fine">
      {cells.map((c, i) => (
        <React.Fragment key={c.l}>
          <div className="flex flex-col items-center min-w-[42px] md:min-w-[64px]">
            <span className="text-2xl md:text-5xl font-extrabold tabular-nums leading-none">
              {String(c.v).padStart(2, '0')}
            </span>
            <span className="text-[9px] md:text-xs font-semibold uppercase tracking-widest opacity-80 mt-1">{c.l}</span>
          </div>
          {i < cells.length - 1 && <span className="text-xl md:text-4xl font-bold opacity-40 -mt-3">:</span>}
        </React.Fragment>
      ))}
    </div>
  );
}

function BannerSlide({ banner, now, onCta }) {
  const hasButton = banner.button_enabled && banner.button_label;
  const cta = hasButton ? (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onCta(banner); }}
      className="inline-flex items-center gap-1.5 rounded-full bg-white text-slate-900 text-xs md:text-sm font-semibold px-4 py-2 shadow-lg hover:bg-slate-100 transition-colors"
      data-testid={`banner-cta-${banner.banner_id}`}
    >
      {banner.button_label} <ArrowRight size={14} />
    </button>
  ) : null;

  // ─── IMAGE banner — respect the uploaded art's aspect ratio edge-to-edge.
  if (banner.type === 'image') {
    const src = imgSrc(banner.main_image_url);
    // Only the tall PRESET hero fallback (3:2) gets the height-capped center
    // crop. An admin-uploaded seasonal banner shares the regular dashboard
    // banner dimensions, so it must render edge-to-edge like every other
    // image banner (below) — otherwise it looks cropped and out of place.
    if (banner.seasonal && banner.hero_fallback && src) {
      return (
        <div
          className="relative w-full h-[200px] md:h-[280px] lg:h-[320px] overflow-hidden"
          data-testid={`banner-slide-${banner.banner_id}`}
        >
          <img src={src} alt={banner.name} className="absolute inset-0 w-full h-full object-cover object-center" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/35 via-transparent to-transparent" />
          {banner.name && (
            <div className="absolute bottom-3 left-4 md:bottom-5 md:left-8 text-white text-lg md:text-2xl font-bold drop-shadow-lg">
              {banner.name}
            </div>
          )}
          {cta && <div className="absolute bottom-3 right-4 md:bottom-5 md:right-8">{cta}</div>}
        </div>
      );
    }
    return (
      <div className="relative w-full" data-testid={`banner-slide-${banner.banner_id}`}>
        {src ? (
          <img src={src} alt={banner.name} className="w-full h-auto block" />
        ) : (
          <div className="w-full aspect-[83/10] bg-gradient-to-r from-slate-800 to-slate-700 flex items-center justify-center text-slate-300 text-sm">
            {banner.name}
          </div>
        )}
        {cta && <div className="absolute bottom-3 right-4 md:bottom-5 md:right-8">{cta}</div>}
      </div>
    );
  }

  // ─── COUNTDOWN banner.
  const targetMs = banner.target_at ? new Date(banner.target_at).getTime() : 0;
  const finished = !targetMs || targetMs - now <= 0;
  const arrivalSrc = imgSrc(banner.arrival_image_url);
  const bgSrc = imgSrc(banner.countdown_image_url);

  if (finished && arrivalSrc) {
    return (
      <div className="relative w-full" data-testid={`banner-slide-${banner.banner_id}`}>
        <img src={arrivalSrc} alt={banner.name} className="w-full h-auto block" />
        {cta && <div className="absolute bottom-3 right-4 md:bottom-5 md:right-8">{cta}</div>}
      </div>
    );
  }

  return (
    <div
      className="relative w-full min-h-[96px] md:min-h-[130px] flex items-center overflow-hidden"
      data-testid={`banner-slide-${banner.banner_id}`}
    >
      {bgSrc ? (
        <img src={bgSrc} alt="" className="absolute inset-0 w-full h-full object-cover" />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-r from-[#0f2547] via-[#13315f] to-[#1a4e88]" />
      )}
      <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-black/10" />
      <div className="relative z-10 flex items-center justify-between w-full px-5 md:px-10 py-3 text-white gap-4">
        <div className="min-w-0">
          {banner.label && (
            <div className="text-sm md:text-xl font-bold truncate drop-shadow mb-1 md:mb-2">{banner.label}</div>
          )}
          {finished ? (
            <div className="text-2xl md:text-4xl font-extrabold drop-shadow" data-testid="banner-countdown-arrived">
              {banner.arrival_label || "It's here!"}
            </div>
          ) : (
            <CountdownValue targetAt={banner.target_at} now={now} />
          )}
        </div>
        {cta}
      </div>
    </div>
  );
}

export default function DashboardBannerCarousel() {
  const [banners, setBanners] = useState([]);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const navigate = useNavigate();
  const hasCountdown = banners.some((b) => b.type === 'countdown');
  const now = useNow(hasCountdown);

  useEffect(() => {
    let cancelled = false;
    // Load admin banners and the active seasonal banner in parallel. The
    // seasonal banner (if any) JOINS the carousel at the front — it never
    // replaces the admin-managed banners.
    Promise.allSettled([dashboardBannerAPI.active(), seasonalThemeAPI.active()])
      .then(([bannersRes, seasonRes]) => {
        if (cancelled) return;
        const admin = bannersRes.status === 'fulfilled' && Array.isArray(bannersRes.value.data)
          ? bannersRes.value.data : [];
        const theme = seasonRes.status === 'fulfilled' ? seasonRes.value.data : null;
        const seasonal = [];
        const heroFallback = theme ? presetHero(theme.template) : null;
        if (theme && theme.banner_enabled && (theme.banner_image_url || heroFallback || theme.banner_label)) {
          seasonal.push({
            seasonal: true,
            hero_fallback: !theme.banner_image_url && !!heroFallback,
          banner_id: `seasonal-${theme.theme_id}`,
            type: 'image',
            name: theme.banner_label || theme.name,
            main_image_url: theme.banner_image_url || heroFallback || null,
            button_enabled: !!theme.button_enabled,
            button_label: theme.button_label,
            button_link_type: theme.button_link_type,
            button_link_value: theme.button_link_value,
          });
        }
        setBanners([...seasonal, ...admin]);
      })
      .catch(() => { if (!cancelled) setBanners([]); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (paused || banners.length <= 1) return undefined;
    const t = setInterval(() => setIndex((i) => (i + 1) % banners.length), ROTATE_MS);
    return () => clearInterval(t);
  }, [paused, banners.length]);

  useEffect(() => {
    if (index >= banners.length) setIndex(0);
  }, [banners.length, index]);

  const onCta = useCallback((banner) => {
    const val = (banner.button_link_value || '').trim();
    if (!val) return;
    if (banner.button_link_type === 'external' || /^https?:\/\//i.test(val)) {
      window.open(val, '_blank', 'noopener,noreferrer');
    } else {
      navigate(val.startsWith('/') ? val : `/${val}`);
    }
  }, [navigate]);

  if (!banners.length) return null;
  const current = banners[Math.min(index, banners.length - 1)];

  return (
    <div
      className="-mt-4 md:-mt-8 -mx-4 md:-mx-8 mb-6 relative select-none"
      data-testid="dashboard-banner-carousel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div key={current.banner_id} className="animate-fadeIn">
        <BannerSlide banner={current} now={now} onCta={onCta} />
      </div>

      {banners.length > 1 && (
        <>
          <button
            type="button"
            onClick={() => setIndex((i) => (i - 1 + banners.length) % banners.length)}
            className="absolute left-2 top-1/2 -translate-y-1/2 z-20 bg-black/35 hover:bg-black/55 text-white rounded-full p-1.5 transition-colors"
            aria-label="Previous banner"
            data-testid="banner-prev-btn"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            type="button"
            onClick={() => setIndex((i) => (i + 1) % banners.length)}
            className="absolute right-2 top-1/2 -translate-y-1/2 z-20 bg-black/35 hover:bg-black/55 text-white rounded-full p-1.5 transition-colors"
            aria-label="Next banner"
            data-testid="banner-next-btn"
          >
            <ChevronRight size={18} />
          </button>
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 z-20 flex items-center gap-1.5">
            {banners.map((b, i) => (
              <button
                key={b.banner_id}
                type="button"
                onClick={() => setIndex(i)}
                className={`h-1.5 rounded-full transition-all ${i === index ? 'w-5 bg-white' : 'w-1.5 bg-white/50 hover:bg-white/80'}`}
                aria-label={`Go to banner ${i + 1}`}
                data-testid={`banner-dot-${i}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
